import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFViewer,
  Image,
  pdf,
} from "@react-pdf/renderer";
import { toast } from 'react-toastify';
// Logo path - uses local PNG file for PDF renderer compatibility
const logo = `${process.env.PUBLIC_URL}/listenhear_logo.png`;

const ReceiptGenerator = ({ deposit, client, onClose }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);

  const handleEmailReceipt = async () => {
    try {
      console.log(`[Receipt Generator] Generating receipt PDF for deposit ${deposit._id}`);

      // Generate PDF blob
      const blob = await pdf(
        <ReceiptDocument
          deposit={deposit}
          client={client}
        />
      ).toBlob();

      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = async () => {
        const base64data = reader.result.split(",")[1];

        console.log(`[Receipt Generator] Sending receipt email to ${client.email}`);

        // TODO: Create a thunk for sending receipt emails
        // For now, just show success message
        toast.success("Receipt generation complete - Email functionality to be implemented");
      };
    } catch (error) {
      console.error("[Receipt Generator] Error generating receipt:", error);
      toast.error("Failed to generate receipt. Please try again.");
    }
  };

  const handlePrintReceipt = () => {
    console.log(`[Receipt Generator] Print receipt requested for deposit ${deposit._id}`);
    toast.info("Use the print button in the PDF viewer below");
  };

  console.log("[Receipt Generator] Rendering receipt for deposit:", deposit);
  console.log("[Receipt Generator] Client data:", client);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-sm shadow-lg border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Deposit Receipt</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Action Buttons */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex gap-3">
            <button
              onClick={handlePrintReceipt}
              className="inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-sm shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 transition-colors">
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Receipt
            </button>

            <button
              onClick={handleEmailReceipt}
              className="inline-flex items-center justify-center px-4 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-sm shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600 transition-colors">
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.83 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email Receipt
            </button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="p-6">
          <PDFViewer
            style={{
              width: "100%",
              height: "600px",
              border: "none",
            }}>
            <ReceiptDocument
              deposit={deposit}
              client={client}
            />
          </PDFViewer>
        </div>
      </div>
    </div>
  );
};

export default ReceiptGenerator;

const ReceiptDocument = ({ deposit, client }) => {
  console.log("[Receipt Document] Rendering PDF for deposit:", deposit);
  console.log("[Receipt Document] Client data:", client);

  const clinic = deposit?.clinic || {};
  const address = client?.address || {};

  // Debug logging for clinic data
  console.log("[Receipt Document] Clinic data:", clinic);
  console.log("[Receipt Document] Clinic name:", clinic.name);
  console.log("[Receipt Document] Clinic address:", clinic.address);
  console.log("[Receipt Document] Clinic phoneNumber:", clinic.phoneNumber);

  // Debug logging for deposit usage
  console.log("[Receipt Document] Deposit usage count:", deposit?.depositUsage?.length || 0);
  if (deposit?.depositUsage && deposit.depositUsage.length > 0) {
    deposit.depositUsage.forEach((usage, index) => {
      console.log(`[Receipt Document] Usage ${index}:`, {
        invoiceFromObject: usage.invoice?.invoiceId,
        invoiceNumber: usage.invoiceNumber,
        amountUsed: usage.amountUsed,
        dateUsed: usage.dateUsed
      });
    });
  }

  // Calculate available balance
  const depositAmount = parseFloat(deposit?.amount) || 0;
  const usedAmount = deposit?.depositUsage?.reduce((sum, usage) => sum + (parseFloat(usage.amountUsed) || 0), 0) || 0;
  const refundAmount = deposit?.refund ? (parseFloat(deposit.refund.refundAmount) || 0) : 0;
  const availableBalance = depositAmount - usedAmount - refundAmount;

  const formattedDate = deposit?.paymentDate
    ? new Date(deposit.paymentDate).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "";

  const formattedTime = deposit?.createdAt
    ? new Date(deposit.createdAt).toLocaleTimeString("en-US", {
        timeStyle: "long",
      })
    : "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header with logo */}
        <View style={styles.header}>
          {logo ? (
            <Image style={styles.logo} src={logo} />
          ) : (
            <Text style={styles.text}>Logo Missing</Text>
          )}
          <View style={{ alignItems: "flex-end" }}>
            <Text style={{ fontSize: 12, fontWeight: "bold" }}>
              {clinic.name || "Clinic Name"}
            </Text>
            <Text style={styles.text}>{clinic.address?.street || ""}</Text>
            <Text style={styles.text}>
              {`${clinic.address?.city || ""}, ${
                clinic.address?.state || ""
              }, ${clinic.address?.zipCode || ""}`}
            </Text>
            <Text style={styles.text}>{clinic.phoneNumber || ""}</Text>
          </View>
        </View>

        {/* Receipt Title and Number */}
        <View style={styles.receiptTitle}>
          <Text style={{ fontSize: 24, fontWeight: "bold" }}>DEPOSIT RECEIPT</Text>
          <Text style={{ ...styles.text, marginTop: 5 }}>
            Receipt #: {deposit._id?.slice(-8).toUpperCase() || "N/A"}
          </Text>
          <Text style={styles.text}>
            Date: {formattedDate}
          </Text>
        </View>

        {/* Client Information */}
        <View style={styles.section}>
          <View style={styles.box}>
            <View style={styles.boxHeader}>
              <Text style={styles.text}>Received From</Text>
            </View>
            <View style={styles.boxContent}>
              <Text style={styles.text}>
                {`${client?.firstName || ""} ${client?.lastName || ""}`.trim()}
              </Text>
              <Text style={styles.text}>
                {address.aptUnit ? `${address.aptUnit} - ` : ""}
                {`${address.streetNumber || ""} ${address.streetName || ""}`}
              </Text>
              <Text style={styles.text}>
                {`${address.city || ""}, ${address.state || ""}, ${address.zipCode || ""}`}
              </Text>
            </View>
          </View>
        </View>

        {/* Payment Details Table */}
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={{ ...styles.tableColHeader, width: "70%" }}>
              <Text style={styles.tableCell}>Description</Text>
            </View>
            <View style={{ ...styles.tableColHeader, width: "30%" }}>
              <Text style={styles.tableCell}>Amount</Text>
            </View>
          </View>

          {/* Deposit Amount Row */}
          <View style={styles.tableRow}>
            <View style={{ ...styles.tableCol, width: "70%" }}>
              <Text style={styles.tableCell}>
                Deposit Payment ({deposit?.paymentMethod?.charAt(0).toUpperCase() + deposit?.paymentMethod?.slice(1) || 'Unknown Method'})
              </Text>
            </View>
            <View style={{ ...styles.tableCol, width: "30%", alignItems: "flex-end" }}>
              <Text style={styles.tableCell}>
                ${depositAmount.toFixed(2)}
              </Text>
            </View>
          </View>

          {/* Deposit Usage Rows - Invoices Applied To */}
          {deposit?.depositUsage && deposit.depositUsage.length > 0 && deposit.depositUsage.map((usage, index) => (
            <View key={index} style={styles.tableRow}>
              <View style={{ ...styles.tableCol, width: "70%" }}>
                <Text style={styles.tableCell}>
                  Invoice {usage.invoice?.invoiceId || usage.invoiceNumber || 'N/A'}
                  {usage.dateUsed && ` (${new Date(usage.dateUsed).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })})`}
                </Text>
              </View>
              <View style={{ ...styles.tableCol, width: "30%", alignItems: "flex-end" }}>
                <Text style={styles.tableCell}>
                  -${(parseFloat(usage.amountUsed) || 0).toFixed(2)}
                </Text>
              </View>
            </View>
          ))}

          {/* Deposit Remaining Row */}
          <View style={styles.tableRow}>
            <View
              style={{
                ...styles.tableCol,
                width: "70%",
                borderRightWidth: 1,
                alignItems: "flex-end",
              }}>
              <Text style={{ ...styles.tableCell, fontWeight: "bold" }}>
                Deposit Remaining
              </Text>
            </View>
            <View
              style={{
                ...styles.tableCol,
                width: "30%",
                alignItems: "flex-end",
              }}>
              <Text style={{ ...styles.tableCell, fontWeight: "bold" }}>
                ${availableBalance.toFixed(2)}
              </Text>
            </View>
          </View>
        </View>

        {/* Refund Information - Only show if deposit has been refunded */}
        {refundAmount > 0 && (
          <View style={{ marginTop: 20, padding: 10, borderWidth: 1, borderColor: "#EF4444", backgroundColor: "#FEE2E2" }}>
            <Text style={{ ...styles.text, fontWeight: "bold", marginBottom: 5, color: "#991B1B" }}>
              Refund Information
            </Text>
            <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 3 }}>
              <Text style={styles.text}>Refund Amount:</Text>
              <Text style={styles.text}>${refundAmount.toFixed(2)}</Text>
            </View>
            {deposit?.refund?.refundDate && (
              <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 3 }}>
                <Text style={styles.text}>Refund Date:</Text>
                <Text style={styles.text}>
                  {new Date(deposit.refund.refundDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </Text>
              </View>
            )}
            {deposit?.refund?.refundMethod && (
              <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 3 }}>
                <Text style={styles.text}>Refund Method:</Text>
                <Text style={styles.text}>{deposit.refund.refundMethod}</Text>
              </View>
            )}
          </View>
        )}

        {/* Notes Section */}
        {availableBalance > 0 && (
          <View style={{ marginTop: 20 }}>
            <Text style={{ ...styles.text, fontSize: 10, fontStyle: "italic" }}>
              {deposit?.depositUsage && deposit.depositUsage.length > 0
                ? `Available balance of $${availableBalance.toFixed(2)} can be applied to future invoices.`
                : `Deposit balance of $${availableBalance.toFixed(2)} available for use on future invoices.`}
            </Text>
          </View>
        )}

        {/* Signature */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
            marginTop: 50,
          }}>
          <Text style={styles.text}>
            Received By: ________________________________
          </Text>
          <Text style={styles.text}>
            Date: ________________________________
          </Text>
        </View>

        {/* Footer */}
        <View
          style={{
            flex: 1,
            justifyContent: "flex-end",
            alignItems: "center",
          }}>
          <Text style={{ fontSize: 8 }}>
            [Receipt generated on {new Date().toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })} at {formattedTime}]
          </Text>
        </View>
      </Page>
    </Document>
  );
};

// Styles for the PDF document
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 30,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  logo: {
    maxWidth: 180,
    maxHeight: 100,
    width: "auto",
    height: "auto",
  },
  receiptTitle: {
    alignItems: "center",
    marginBottom: 30,
    paddingBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#4F46E5",
  },
  section: {
    marginBottom: 20,
  },
  box: {
    borderWidth: 1,
    marginBottom: 20,
  },
  boxHeader: {
    borderBottomWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: "#F3F4F6",
  },
  boxContent: {
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  text: {
    fontSize: 12,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginTop: 20,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableColHeader: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#D3D3D3",
  },
  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCell: {
    margin: 5,
    fontSize: 10,
  },
});
